import { format, parseISO } from 'date-fns'
import first from 'lodash/first'

import {
  BillType,
  GetInvoiceQuery,
  InvoiceBill,
  InvoiceOrder,
  InvoiceOrderLineItem,
  InvoiceOrderReversal,
  InvoiceVenue,
  LineItemType,
  OrderingType,
  OrderLineItemSourceType,
  OrderSourceType,
} from '@/gql/graphql'

import type {
  GroupedLineItemsProps,
  GroupedRefundsProps,
  InvoiceProps,
  LineItemWithTaxDependantPrice,
  RefundLineItem,
} from './types'

/**
 * Calculates totals for the invoice.
 * Only simple maths here, e.g summing line items and accounting refunds.
 * Anything more complex should be done on the backend.
 */
const calculateTotals = (
  orders: InvoiceOrder[],
  orderReversals: InvoiceOrderReversal[],
) => {
  const { paidTotalIncludingTaxInCents, paidTotalTaxInCents } = orders.reduce(
    (acc, order) => {
      acc.paidTotalIncludingTaxInCents += order.totalIncludingTaxAmount
      acc.paidTotalTaxInCents += order.totalTaxAmount
      return acc
    },
    { paidTotalIncludingTaxInCents: 0, paidTotalTaxInCents: 0 },
  )

  const { refundedTotalIncludingTaxInCents, refundedTotalTaxInCents } =
    orderReversals.reduce(
      (acc, order) => {
        acc.refundedTotalIncludingTaxInCents += order.totalIncludingTaxAmount
        acc.refundedTotalTaxInCents += order.totalTaxAmount
        return acc
      },
      { refundedTotalIncludingTaxInCents: 0, refundedTotalTaxInCents: 0 },
    )

  const netTotalIncludingTaxInCents =
    paidTotalIncludingTaxInCents + refundedTotalIncludingTaxInCents
  const netTotalTaxInCents = paidTotalTaxInCents + refundedTotalTaxInCents

  return {
    paidTotalIncludingTaxInCents,
    paidTotalTaxInCents,
    refundedTotalIncludingTaxInCents,
    refundedTotalTaxInCents,
    netTotalIncludingTaxInCents,
    netTotalTaxInCents,
  }
}

/**
 * Grouping line items per venue and guest into format expected by the page.
 */
export const groupLineItems = ({
  bill,
  orders,
  collectionId,
  referenceId,
}: {
  bill: InvoiceBill
  orders: InvoiceOrder[]
  collectionId?: string // Populated if it is in the route
  referenceId?: string // Populated if it is in the route
}): GroupedLineItemsProps[] => {
  const groupedItems: GroupedLineItemsProps[] = []

  for (const order of orders) {
    // For tax exclusive venues we are showing line items tax exclusive
    const items = (order.lineItems || []).map((li) => ({
      ...li,
      priceTaxDependentInCents: order.venue.isTaxInclusive
        ? li.unitAmountIncludingTax
        : li.unitAmountIncludingTax - li.unitTaxAmount,
    }))

    const splitItems = separateAdditionalLineItems(items)

    // For O&P (private bills), grouping should be undefined (falls back to "Your order")
    // For O&P group ordering, name of the group should be guest name
    // For O&P participants (private bill), name of group is undefined ("Your order")
    // For MVO, group name is venue
    // For MVO group ordering, if we are looking at a specific child MVO Order invoice, it should be undefined ("Your order", collectionId+referenceId)
    // For MVO group ordering, if we are looking at a specific operator level MVO order invoice, it should be venue name (by collectionId)
    // For MVO group ordering, if we are looking at the whole group order, group by guest name
    const determineGroupTitle = () => {
      // Case 1: Both bill types when collection and reference IDs exist
      if (
        order.source === OrderSourceType.MeanduMvoOrder &&
        collectionId &&
        referenceId
      ) {
        return undefined
      }

      // Case 2: Shared bill with MeanduMvoOrder and collectionId
      if (
        bill.type === BillType.Shared &&
        order.source === OrderSourceType.MeanduMvoOrder &&
        collectionId
      ) {
        return order.venue.name
      }

      // Case 3: Shared bill with any other source
      if (bill.type === BillType.Shared) {
        return order.guest?.name ?? ''
      }

      // Case 4: Private bill with MeanduMvoOrder
      if (
        bill.type === BillType.Private &&
        order.source === OrderSourceType.MeanduMvoOrder
      ) {
        return order.venue.name
      }

      // Case 5: Default case - all other scenarios
      return undefined
    }

    groupedItems.push({
      groupTitle: determineGroupTitle(),
      groupSlug: order.venue.slug,
      isMvo: order.source === OrderSourceType.MeanduMvoOrder,
      collectionId: order.collectionId,
      referenceId: order.id,
      orderId: order.id,
      menuItems: splitItems.menuItems,
      additionalItems: splitItems.additionalItems,
    })
  }

  return groupedItems
}

/**
 * Separates additional line items such as gratuity, card fee, service charge, etc. from
 * menu items. And sorts them.
 */
export const separateAdditionalLineItems = (
  items: LineItemWithTaxDependantPrice[],
) => {
  const additionalItems = []
  const menuItems = []

  for (const item of items) {
    switch (item.type) {
      case LineItemType.Gratuity:
      case LineItemType.AbsoluteDiscount:
      case LineItemType.CardFee:
      case LineItemType.DeliveryFee:
      case LineItemType.ServiceCharge:
        additionalItems.push(item)
        break
      default:
        menuItems.push(item)
    }
  }

  return {
    menuItems: menuItems.sort((a, b) => a.name.localeCompare(b.name)),
    additionalItems: sortLineItems(additionalItems),
  }
}

/**
 * Groups refunds per day. Also flattens line items of same type.
 * E.g. if there are two refunds in the same day each for one coffee,
 * it will show as one refund for two coffees.
 */
export const groupOrderReversalsByDay = (
  venue: InvoiceVenue,
  orders: InvoiceOrder[],
  orderReversals: InvoiceOrderReversal[],
): GroupedRefundsProps[] => {
  // Reversal modifier LIs only have reference to original LIs (via `parentId`), so we need them for lookup
  // as we still need to display parent item name even if only modifier is reversed
  const originalLIs = orders
    .flatMap((o) => o.lineItems)
    .reduce(
      (acc, li) => {
        acc[li.id] = li
        return acc
      },
      {} as Record<string, InvoiceOrderLineItem>,
    )

  const groupedReversals = orderReversals.reduce(
    (acc, reversal) => {
      const day = format(parseISO(reversal.createdAt), 'yyyy-MM-dd')
      if (!acc[day]) {
        acc[day] = []
      }
      acc[day].push(reversal)
      return acc
    },
    {} as Record<string, InvoiceOrderReversal[]>,
  )

  const refunds: GroupedRefundsProps[] = []
  for (const [day, reversals] of Object.entries(groupedReversals)) {
    const customRefund = reversals
      .filter((r) => r.reversalType === 'CUSTOM')
      .reduce(
        (acc, r) => {
          if (acc) {
            acc.unitAmountIncludingTax += r.totalIncludingTaxAmount
            acc.unitTaxAmount += r.totalTaxAmount
            acc.priceTaxDependentInCents += venue.isTaxInclusive
              ? r.totalIncludingTaxAmount
              : r.totalIncludingTaxAmount - r.totalTaxAmount
            return acc
          }

          acc = {
            createdAt: r.createdAt,
            id: r.id,
            parentId: null,
            source: OrderLineItemSourceType.MeanduOther,
            name: 'custom refund',
            quantity: 1,
            type: 'CUSTOM_REFUND',
            unitAmountIncludingTax: r.totalIncludingTaxAmount,
            unitTaxAmount: r.totalTaxAmount,
            priceTaxDependentInCents: venue.isTaxInclusive
              ? r.totalIncludingTaxAmount
              : r.totalIncludingTaxAmount - r.totalTaxAmount,
          }
          return acc
        },
        undefined as undefined | RefundLineItem,
      )

    const allReversalLineItems = reversals.flatMap((r) => r.lineItems)
    const flatReversalLineItemsMap = allReversalLineItems.reduce(
      (acc, li) => {
        const key = [
          OrderLineItemSourceType.MeanduMenuItem,
          OrderLineItemSourceType.MeanduMenuItemModifier,
          OrderLineItemSourceType.MeanduMenuItemUpsell,
        ].includes(li.source)
          ? `${li.source}-${li.sourceReferenceId}-${li.notes || ''}`
          : `${li.id}`

        if (!acc[key]) {
          acc[key] = {
            ...li,
            priceTaxDependentInCents: venue.isTaxInclusive
              ? li.unitAmountIncludingTax
              : li.unitAmountIncludingTax - li.unitTaxAmount,
          }
        } else {
          acc[key].quantity += li.quantity
        }
        return acc
      },
      {} as Record<string, RefundLineItem>,
    )

    // If only child item is reversed, we still need to provide parent for display
    ensureChildItemsHaveParent(originalLIs, flatReversalLineItemsMap)

    const lineItems = [
      ...Object.values(flatReversalLineItemsMap),
      ...(customRefund ? [customRefund] : []),
    ]

    refunds.push({
      date: day,
      lineItems: sortLineItems(lineItems),
    })
  }

  // sort refunds by date ascending
  return refunds.sort((a, b) => a.date.localeCompare(b.date))
}

/**
 * Ensures that all child items have a parent item in the list of line items.
 * This is needed to display parent item even if only child is refunded.
 */
const ensureChildItemsHaveParent = (
  orderLIsMap: Record<string, InvoiceOrderLineItem>,
  reversalLIsMap: Record<string, RefundLineItem>,
) => {
  const reversalLIs = Object.values(reversalLIsMap)

  const reversalLIsBySourceReferenceIdMap = reversalLIs.reduce(
    (acc, li) => {
      if (li.sourceReferenceId) {
        acc[li.sourceReferenceId] = li
      }
      return acc
    },
    {} as Record<string, RefundLineItem>,
  )

  for (const li of reversalLIs) {
    // No child/parent relationship. Nothing to do.
    if (!li.parentId) {
      continue
    }

    // Parent is already in the list. Nothing to do.
    if (reversalLIsMap[li.parentId]) {
      continue
    }

    const originalParentLI = orderLIsMap[li.parentId]
    // We can't find parent. We insert empty one, so at least child will be displayed.
    if (!originalParentLI) {
      reversalLIsMap[li.parentId] = {
        id: li.parentId,
        source: OrderLineItemSourceType.MeanduOther,
        type: LineItemType.MenuItem,
        name: '',
        quantity: 0,
        unitAmountIncludingTax: 0,
        unitTaxAmount: 0,
        priceTaxDependentInCents: 0,
        createdAt: new Date().toISOString(),
        notRefundedParentItem: true,
      }
      continue
    }

    // First check if matching parent (by sourceReferenceId) is already in the list, if so use that.
    if (
      originalParentLI.sourceReferenceId &&
      reversalLIsBySourceReferenceIdMap[originalParentLI.sourceReferenceId]
    ) {
      li.parentId =
        reversalLIsBySourceReferenceIdMap[originalParentLI.sourceReferenceId].id
      continue
    }

    // Parent is not in the list, so we need to add it.
    reversalLIsMap[originalParentLI.id] = {
      ...originalParentLI,
      notRefundedParentItem: true,
      quantity: 0,
      unitAmountIncludingTax: 0,
      unitTaxAmount: 0,
      priceTaxDependentInCents: 0,
    }
  }
}

/**
 * Flattens gratuity, card fee and service charges into a single line
 * item each. But, keeping other additional line item types e.g.
 * AbsoluteDiscount, ServiceCharge,.. etc as is.
 *
 * Used on the page to show the summary of those. It is needed when
 * they are split across multiple orders e.g. MVO.
 */
export const flattenAdditionalLineItems = (groups: GroupedLineItemsProps[]) => {
  const additionalItems = []

  let gratuityItem: LineItemWithTaxDependantPrice | undefined = undefined
  let cardFeeItem: LineItemWithTaxDependantPrice | undefined = undefined
  let serviceChargeItem: LineItemWithTaxDependantPrice | undefined = undefined

  for (const group of groups) {
    for (const item of group.additionalItems) {
      switch (item.type) {
        case LineItemType.Gratuity: {
          let gPriceInCents: number = gratuityItem?.unitAmountIncludingTax ?? 0
          gPriceInCents += item.unitAmountIncludingTax

          gratuityItem = {
            ...item,
            unitAmountIncludingTax: gPriceInCents,
          }
          break
        }
        case LineItemType.CardFee: {
          let cPriceInCents: number = cardFeeItem?.unitAmountIncludingTax ?? 0
          cPriceInCents += item.unitAmountIncludingTax

          cardFeeItem = {
            ...item,
            unitAmountIncludingTax: cPriceInCents,
          }
          break
        }
        case LineItemType.ServiceCharge: {
          let sPriceInCents: number =
            serviceChargeItem?.unitAmountIncludingTax ?? 0
          sPriceInCents += item.unitAmountIncludingTax

          serviceChargeItem = {
            ...item,
            unitAmountIncludingTax: sPriceInCents,
          }
          break
        }
        default:
          additionalItems.push(item)
          break
      }
    }
  }

  if (cardFeeItem) {
    additionalItems.push(cardFeeItem)
  }

  if (gratuityItem) {
    additionalItems.push(gratuityItem)
  }

  if (serviceChargeItem) {
    additionalItems.push(serviceChargeItem)
  }

  return sortLineItems(additionalItems)
}

/**
 * Sort line items alphabetically grouped by type. Note, additional items are normally displayed
 * separately from menu items, but they are together in the same list for refunds.
 */
const sortLineItems = <
  T extends LineItemWithTaxDependantPrice | RefundLineItem,
>(
  items: T[],
): T[] => {
  const typeToOrder = (li: LineItemWithTaxDependantPrice | RefundLineItem) => {
    switch (li.type) {
      case LineItemType.AbsoluteDiscount:
        // Discounts first then absolute surcharges
        return li.unitAmountIncludingTax < 0 ? `1-${li.name}` : `2-${li.name}`
      case LineItemType.ServiceCharge:
        return `3-${li.name}`
      case LineItemType.DeliveryFee:
        return `4-${li.name}`
      case LineItemType.CardFee:
        return `5-${li.name}`
      case LineItemType.Gratuity:
        return `6-${li.name}`
      case 'CUSTOM_REFUND':
        return `7-${li.name}`
      default:
        return `0-${li.name}`
    }
  }

  return items.sort((a, b) => typeToOrder(a).localeCompare(typeToOrder(b)))
}

/**
 * Returns display options for the invoice based on the data.
 */
const displayOptions = ({
  referenceId,
  bill,
  orders,
}: {
  referenceId?: string
  bill: InvoiceBill
  orders: InvoiceOrder[]
}) => {
  const isMvo = !!orders.find((o) => o.source === 'MEANDU_MVO_ORDER')

  // Show links where there are multiple orders in collection and this is not a group order i.e. shared bill.
  // Note: Technically we can easily show links for group orders as well, but just matching the rules with legacy invoice.
  const showLinksToChildVenueInvoices = isMvo && !referenceId
  //move this to suborder

  // Just matching with legacy invoice, where for some reason for MVO we don't want to show notes
  const showNotes = !isMvo || !!referenceId

  // Legacy invoice shows ID = legacyOrderId or legacy mvoOrderId for ZAR currency.
  // I assume for troubleshooting purposes, anyway, keeping it there. However, this would be billId going forward.
  const showInvoiceId = bill.currency === 'ZAR'

  return {
    showInvoiceId,
    showLinksToChildVenueInvoices,
    showNotes,
  }
}

export const processData = ({
  billId,
  collectionId,
  referenceId,
  data,
}: {
  billId: string
  collectionId?: string
  referenceId?: string
  data: GetInvoiceQuery
}): InvoiceProps => {
  const {
    guest,
    bill,
    orders: allOrders,
    orderReversals,
    venue,
  } = data.getInvoice

  // This will get filtered on the backend, but duplicating it here to avoid any caching issues
  const orders = allOrders.filter((order) => {
    if (collectionId && order.collectionId !== collectionId) {
      return false
    }
    if (referenceId && order.id !== referenceId) {
      return false
    }
    return true
  })

  const firstOrder = first(orders)
  if (!firstOrder) {
    return {}
  }

  // Below invoice data is retrieved from first order. When there are multiple orders on one invoice
  // (i.e. MVO or group ordering), then most details would be the same across all e.g. ordering type,
  // however there could be diffs as well, e.g. table number. We will improve upon this in future if
  // needed.
  const invoiceDetails = {
    orderingType: firstOrder.orderingType as OrderingType,
    createdAt: firstOrder.createdAt,
    orderingWindowStartDate: firstOrder.orderingWindowStartDate,
    tableNumber: firstOrder.tableNumber,
    deliveryOption: firstOrder.deliveryOption,
    pickupOption: firstOrder.pickupOption,
    orderNotes: firstOrder.orderNotes,
  }

  // POS Ids for the reference
  const batchPosDisplayIds = [
    ...new Set(orders.flatMap((o) => o.batchPosDisplayIds || [])),
  ]
  const totals = calculateTotals(orders, orderReversals)

  const invoice = {
    id: billId,
    batchPosDisplayIds: batchPosDisplayIds.length ? batchPosDisplayIds : null,
    ...invoiceDetails,
    ...totals,
  }

  const lineItems = groupLineItems({
    bill,
    orders,
    collectionId,
    referenceId,
  })
  const refunds = groupOrderReversalsByDay(venue, orders, orderReversals)
  const options = displayOptions({ referenceId, bill, orders })

  return {
    data: {
      guest,
      invoice,
      bill,
      lineItems,
      flatAdditionalLineItems: flattenAdditionalLineItems(lineItems),
      refunds,
      venue, // for MVO this is parent venue
    },
    options,
  }
}
